import { useToolOwnership } from "../shared/toolOwnership"
import { getAuthGeneration } from "utils/auth"
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { meetings } from "services/api/meetingsClient"
import type { MeetingMessagesPanelResponse, MeetingPanelEventPayload } from "services/api/meetingsClient"
import {
  readMeetingMessagesActiveThread,
  subscribeToMeetingMessagesActiveThread,
  type MeetingMessagesActiveThread,
} from "../../meetingMessagesOpenCommand"
import { useMeetingPanelEvents } from "../../useMeetingPanelEvents"
import type { CallSurfaceHandle, DirectMessageToast, JoinInfo, MessageOpenTarget } from "../types"
import { getPanelTokenFromUrl } from "../shared/panelUrls"
import { findThreadForMessageEvent, getMessagesPanelUnreadCount, wait } from "../shared/messagesPanel"

const MESSAGE_NOTIFICATION_READ_RECONCILE_DELAY_MS = 350
const MESSAGE_TOAST_DURATION_MS = 8000
const UNREAD_REFRESH_INTERVAL_MS = 60000

type UseMessagesNotificationsArgs = {
  /** Receives `meeting.*` realtime events (host ended or cancelled the meeting). */
  onMeetingEvent?: (event: { type?: string }) => void
  meetingId?: string
  joinInfo: JoinInfo | null
  surfaceRef: RefObject<CallSurfaceHandle | null>
}

/**
 * Direct-message unread count, realtime notifications, and the "open Messages" command
 * shared by both renderers. The active surface decides how Messages is presented and answers
 * whether it is currently open through the surface handle.
 */
export function useMessagesNotifications({ meetingId, joinInfo, surfaceRef, onMeetingEvent }: UseMessagesNotificationsArgs) {
  const onMeetingEventRef = useRef(onMeetingEvent)
  onMeetingEventRef.current = onMeetingEvent
  const [unreadCount, setUnreadCount] = useState(0)
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null)
  const [toast, setToast] = useState<DirectMessageToast | null>(null)
  const identityKeyRef = useRef("")
  const activeThreadRef = useRef<MeetingMessagesActiveThread | null>(null)
  const surfaceOpenRef = useRef(false)
  const toastTimerRef = useRef<number | null>(null)
  const panelUrl = joinInfo?.messages_panel?.url
  const panelToken = useMemo(() => getPanelTokenFromUrl(panelUrl), [panelUrl])
  const logicalOwner = joinInfo?.native_tools_expires_at ? JSON.stringify([meetingId, joinInfo.native_entry_snapshot?.accessId, joinInfo.guest_session_id, joinInfo.messages_panel?.role]) : `${meetingId}:${panelToken}`
  const { key: ownerKey, capture } = useToolOwnership(logicalOwner)
  const summaryOwner = useRef(ownerKey)
  const toastOwner = useRef(ownerKey)
  const panelScope = `${ownerKey}:${panelToken}`
  const currentPanel = useRef(panelScope); currentPanel.current = panelScope
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const enabled = Boolean(joinInfo?.messages_panel?.enabled && meetingId && panelToken)

  useEffect(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
      toastTimerRef.current = null
    }
    if (!toast) return
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null)
      toastTimerRef.current = null
    }, MESSAGE_TOAST_DURATION_MS)
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
        toastTimerRef.current = null
      }
    }
  }, [toast])

  useEffect(() => {
    if (!meetingId || !panelToken || !enabled) {
      activeThreadRef.current = null
      return
    }
    return subscribeToMeetingMessagesActiveThread(meetingId, panelToken, (activeThread) => {
      activeThreadRef.current = activeThread
    })
  }, [enabled, meetingId, panelToken])

  const applySummary = useCallback((response: MeetingMessagesPanelResponse) => {
    summaryOwner.current = ownerKey
    identityKeyRef.current = response.identity_key || ""
    setRealtimeUrl(response.realtime_url || null)
    setUnreadCount(getMessagesPanelUnreadCount(response))
  }, [ownerKey])

  const refreshUnreadCount = useCallback(async () => {
    if (!meetingId || !panelToken || !enabled) {
      setUnreadCount(0)
      setRealtimeUrl(null)
      identityKeyRef.current = ""
      return null
    }
    try {
      const response = await meetings.getMeetingMessagesPanel(meetingId, panelToken)
      if (!mounted.current || currentPanel.current !== panelScope) return null
      applySummary(response)
      return response
    } catch {
      // The embedded panel still has polling/manual refresh if this lightweight badge refresh fails.
      return null
    }
  }, [applySummary, enabled, meetingId, panelToken, panelScope])

  const handleRealtimeEvent = useCallback(async (event: MeetingPanelEventPayload) => {
    const owns = capture()
    if (event.type !== "direct_message.created" && event.type !== "direct_message.read") return
    const senderIdentityKey = typeof event.sender_identity_key === "string" ? event.sender_identity_key : ""
    if (event.type === "direct_message.created" && senderIdentityKey && senderIdentityKey === identityKeyRef.current) {
      return
    }
    if (event.type === "direct_message.created") {
      await wait(MESSAGE_NOTIFICATION_READ_RECONCILE_DELAY_MS)
    }
    if (!owns()) return
    const response = await refreshUnreadCount()
    if (!owns()) return
    if (event.type !== "direct_message.created" || !response) return
    if (senderIdentityKey && senderIdentityKey === response.identity_key) {
      return
    }
    const thread = findThreadForMessageEvent(response, event)
    const lastMessage = thread?.last_message
    if (!thread || !lastMessage || lastMessage.is_own || !lastMessage.body.trim()) {
      return
    }
    if ((thread.unread_count || 0) <= 0) {
      return
    }
    const activeThread = (
      meetingId && panelToken
        ? readMeetingMessagesActiveThread(meetingId, panelToken)
        : null
    ) || activeThreadRef.current
    const surfaceOpen = surfaceRef.current?.isMessagesOpen()
    const messagesSurfaceOpen = surfaceOpen === undefined ? surfaceOpenRef.current : surfaceOpen
    surfaceOpenRef.current = messagesSurfaceOpen
    if (!messagesSurfaceOpen) {
      activeThreadRef.current = null
    }
    if (
      messagesSurfaceOpen &&
      activeThread?.active &&
      (
        activeThread.threadId === thread.id ||
        activeThread.recipientIdentityKey === thread.recipient.identity_key ||
        (senderIdentityKey && activeThread.recipientIdentityKey === senderIdentityKey)
      )
    ) {
      return
    }
    const toastId = `${thread.id}:${lastMessage.id || lastMessage.created_at}`
    toastOwner.current = ownerKey
    setToast((current) => current?.id === toastId ? current : {
      id: toastId,
      threadId: thread.id,
      recipientIdentityKey: thread.recipient.identity_key,
      senderName: thread.recipient.display_name || "New message",
      body: lastMessage.body,
    })
  }, [meetingId, panelToken, refreshUnreadCount, surfaceRef, capture, ownerKey])

  useEffect(() => {
    if (!enabled) {
      setUnreadCount(0)
      setRealtimeUrl(null)
      return
    }
    refreshUnreadCount()
    const intervalId = window.setInterval(refreshUnreadCount, UNREAD_REFRESH_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [enabled, refreshUnreadCount])

  useMeetingPanelEvents({ onReconnect: () => {
    void refreshUnreadCount()
    if (joinInfo?.native_tools_expires_at) onMeetingEventRef.current?.({ type: 'meeting.updates_reconnected' })
  },
    panelToken, renewalKey: joinInfo?.native_tools_expires_at ? JSON.stringify([meetingId, getAuthGeneration(), joinInfo.native_entry_snapshot?.accessId, joinInfo.retained_native_guest, joinInfo.messages_panel?.role]) : undefined,
    realtimeUrl: summaryOwner.current === ownerKey ? realtimeUrl : null,
    enabled,
    onEvent: (event) => {
      if (!mounted.current || currentPanel.current !== panelScope) return
      if (typeof event?.type === "string" && event.type.startsWith("meeting.")) {
        onMeetingEventRef.current?.(event)
        return
      }
      void handleRealtimeEvent(event)
    },
  })

  const setSurfaceOpen = useCallback((open: boolean) => {
    surfaceOpenRef.current = open
    if (!open) {
      activeThreadRef.current = null
    }
  }, [])

  const openMessages = useCallback((target?: MessageOpenTarget) => {
    surfaceRef.current?.openMessages(target)
    setToast(null)
  }, [surfaceRef])

  const dismissToast = useCallback(() => setToast(null), [])

  const reset = useCallback(() => {
    surfaceOpenRef.current = false
    activeThreadRef.current = null
  }, [])

  return { enabled, panelToken, unreadCount: summaryOwner.current === ownerKey ? unreadCount : 0, toast: toastOwner.current === ownerKey ? toast : null, dismissToast, openMessages, setSurfaceOpen, reset, notifyDirectMessageEvent: handleRealtimeEvent }
}
